import { useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { SplashScreen } from 'src/components/loading-screen';

import { useAuthContext } from '../hooks';

// ----------------------------------------------------------------------

const loginPaths: Record<string, string> = {
  jwt: paths.auth.jwt.login,
  auth0: paths.auth.auth0.login
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function AuthGuard({ children }: Props) {
  const { loading } = useAuthContext();

  return <>{loading ? <SplashScreen /> : <Container>{children}</Container>}</>;
}

// ----------------------------------------------------------------------

function Container({ children }: Props) {
  const router = useRouter();
  const { pathname } = useLocation();

  const { authenticated, method, user } = useAuthContext();

  const [checked, setChecked] = useState(false);

  const check = useCallback(() => {
    if (!authenticated) {
      const searchParams = new URLSearchParams({
        returnTo: pathname,
      }).toString();

      const loginPath = loginPaths[method];

      const href = `${loginPath}?${searchParams}`;

      router.replace(href);
    } else {
      const userRole = typeof user?.role === 'string' ? user.role : '';

      // Platform admins (ADR-0002) live above all agencies in the /admin portal. They carry no
      // AgencyId, so the agency-scoped operator app would break for them — funnel them into /admin
      // and keep everyone else out of it (the backend PlatformAdmin policy is the real gate).
      const isPlatformAdmin = userRole.includes('platform-admin');
      const isOnAdminRoute = pathname.startsWith('/admin');

      if (isPlatformAdmin && !isOnAdminRoute) {
        router.replace('/admin/agencies');
        return;
      }

      if (!isPlatformAdmin && isOnAdminRoute) {
        router.replace('/dashboard');
        return;
      }

      // Receptor-only users can only access /reception/* routes
      const isReceptorOnly = userRole.includes('receptor') && !userRole.includes('owner') && !userRole.includes('employee') && !userRole.includes('distribution-client');
      const isOnReceptionRoute = pathname.startsWith('/reception');

      if (isReceptorOnly && !isOnReceptionRoute) {
        router.replace('/reception');
        return;
      }

      // Distribution-client-only users can only access /distribution/* routes
      const isDistributionClientOnly = userRole.includes('distribution-client') && !userRole.includes('owner') && !userRole.includes('employee');
      const isOnDistributionRoute = pathname.startsWith('/distribution');

      if (isDistributionClientOnly && !isOnDistributionRoute) {
        router.replace('/distribution/manifests');
        return;
      }

      // Client-only users (locker portal) can only access /portal/* routes
      const isClientOnly = userRole.includes('client') && !userRole.includes('owner') && !userRole.includes('employee');
      const isOnPortalRoute = pathname.startsWith('/portal');

      if (isClientOnly && !isOnPortalRoute) {
        router.replace('/portal/home');
        return;
      }

      setChecked(true);
    }
  }, [authenticated, method, router, user, pathname]);

  useEffect(() => {
    check();
  }, [check]);

  if (!checked) {
    return null;
  }

  return <>{children}</>;
}
